// Similarity helpers for detecting organizations with a similar name during creation.
//
// The matching is intentionally frontend-only and works in two layers:
//   1. `normalizeOrganizationName` reduces a raw name to a comparable "core"
//      (umlauts transliterated, legal forms and punctuation removed).
//   2. `computeNameSimilarity` scores the normalized input against a candidate.
//
// Scoring the *full* normalized input is what makes short, incomplete input
// (e.g. "mu") score low against long names and drop out via the threshold —
// instead of flooding the warning with every substring hit.

// German and common international legal forms that carry no distinguishing value.
const LEGAL_FORM_TOKENS = new Set([
  'ag',
  'co',
  'corp',
  'ev',
  'ggmbh',
  'gbr',
  'gmbh',
  'inc',
  'kg',
  'kgaa',
  'llc',
  'ltd',
  'mbh',
  'ohg',
  'se',
  'ug',
])

const UMLAUT_REPLACEMENTS: [RegExp, string][] = [
  [/ä/gu, 'ae'],
  [/ö/gu, 'oe'],
  [/ü/gu, 'ue'],
  [/ß/gu, 'ss'],
]

// Unicode combining diacritical marks left over after NFD normalization.
const COMBINING_MARKS = /[\u0300-\u036f]/gu

/**
 * Reduces a raw organization name to a normalized comparison core:
 * lowercased, umlauts/diacritics transliterated, punctuation removed and
 * legal-form tokens stripped. Spaces are removed so token reordering and
 * spacing differences do not affect the comparison.
 */
const normalizeOrganizationName = (name: string): string => {
  let normalized = name.toLowerCase()

  for (const [pattern, replacement] of UMLAUT_REPLACEMENTS) {
    normalized = normalized.replace(pattern, replacement)
  }

  // Strip any remaining diacritics (e.g. é -> e).
  normalized = normalized.normalize('NFD').replace(COMBINING_MARKS, '')

  // Replace everything that is not a latin letter or digit with a separator.
  normalized = normalized.replace(/[^a-z0-9]+/gu, ' ').trim()

  return normalized
    .split(/\s+/u)
    .filter((token) => token.length > 0 && !LEGAL_FORM_TOKENS.has(token))
    .join('')
}

const buildBigramCounts = (value: string): Map<string, number> => {
  const counts = new Map<string, number>()
  for (let index = 0; index < value.length - 1; index += 1) {
    const bigram = value.slice(index, index + 2)
    counts.set(bigram, (counts.get(bigram) ?? 0) + 1)
  }
  return counts
}

/**
 * Sørensen–Dice coefficient over character bigrams (0..1). Robust against
 * partial matches and token reordering without external dependencies.
 */
const diceCoefficient = (first: string, second: string): number => {
  if (first.length < 2 || second.length < 2) {
    return first === second ? 1 : 0
  }

  const firstBigrams = buildBigramCounts(first)
  const secondBigrams = buildBigramCounts(second)

  let intersectionSize = 0
  for (const [bigram, count] of firstBigrams) {
    const otherCount = secondBigrams.get(bigram) ?? 0
    intersectionSize += Math.min(count, otherCount)
  }

  return (2 * intersectionSize) / (first.length - 1 + (second.length - 1))
}

/**
 * Length-weighted containment ratio (0..1). When one string contains the
 * other, the score is the length ratio, so a short fragment inside a long
 * name scores low (e.g. "mu" in "muelichen...") while a near-complete name
 * scores high (e.g. "mueller" in "muellerbau").
 */
const containmentRatio = (first: string, second: string): number => {
  const [shorter, longer] = first.length <= second.length ? [first, second] : [second, first]
  if (longer.length === 0 || !longer.includes(shorter)) {
    return 0
  }
  return shorter.length / longer.length
}

/**
 * Similarity score (0..1) between two already-normalized names.
 * Combines the Dice coefficient with the containment ratio and takes the
 * stronger of the two signals.
 */
const computeNameSimilarity = (normalizedQuery: string, normalizedCandidate: string): number => {
  if (!normalizedQuery || !normalizedCandidate) {
    return 0
  }
  if (normalizedQuery === normalizedCandidate) {
    return 1
  }

  return Math.max(
    diceCoefficient(normalizedQuery, normalizedCandidate),
    containmentRatio(normalizedQuery, normalizedCandidate),
  )
}

export { computeNameSimilarity, normalizeOrganizationName }
